import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect, useRef } from 'react';
import ListingStep from './06-ListingStep';
import DataVerificationStep from './07-DataVerificationStep';
import AIPriceSuggestionStep from './08-AIPriceSuggestionStep';
import BuyerVisitScheduleStep from './09-BuyerVisitScheduleStep';
import SummaryStep from './10-SummaryStep';
import AgreementPaymentPublishStep from './11-AgreementPaymentPublishStep';
import './PropertyWizard.css';

const LISTING_STEPS = [
  { key: 6,  label: 'انتشار آگهی' },
  { key: 7,  label: 'تأیید اطلاعات' },
  { key: 8,  label: 'پیشنهاد قیمت' },
  { key: 9,  label: 'بازدید' },
  { key: 10, label: 'خلاصه' },
  { key: 11, label: 'نهایی‌سازی' },
];

const listingPropertyId = (listing) =>
  typeof listing?.property === 'object' ? listing.property?.id : listing?.property;

const findListingForProperty = (listings, propertyId) => {
  const arr = Array.isArray(listings) ? listings : listings?.results || [];
  return arr.find(l => listingPropertyId(l) === propertyId) || null;
};

const fetchListingForProperty = async (propertyId, authToken) => {
  const res = await apiRequest(
    apiRoutes.listings.byProperty(propertyId),
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  if (!res.ok) return null;
  return findListingForProperty(await res.json(), propertyId);
};

/**
 * ListingCreateModal
 *
 * Full-screen wizard for steps 6–11 of an existing, fully-registered property.
 * Mirrors the PropertyEditModal pattern but handles listing creation flow.
 */
function ListingCreateModal({ property, onClose, onSaved }) {
  const [activeStep, setActiveStep] = useState(6);
  const [stepData, setStepData] = useState({ 6: {}, 7: {}, 8: {}, 9: {}, 10: {}, 11: {} });
  const [listingId, setListingId] = useState(null);
  const [wantList, setWantList] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedSteps, setSavedSteps] = useState(new Set());
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [completed, setCompleted] = useState(false);

  const token = () => localStorage.getItem('access_token');
  const contentRef = useRef(null);

  // On mount, check if a listing already exists for this property
  useEffect(() => {
    const restoreListingId = async () => {
      try {
        const existing = await fetchListingForProperty(property.id, token());
        if (existing) {
          setListingId(existing.id);
        }
      } catch { /* non-fatal */ }
    };
    restoreListingId();
  }, [property.id]);

  const createListing = async (listingType, availableFrom) => {
    const res = await apiRequest(apiRoutes.listings.list, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({
        property: property.id,
        listing_type: listingType,
        available_from: availableFrom,
        listed_by_type: 'OWNER',
        lifecycle_status: 'DRAFT',
        availability_status: 'AVAILABLE',
      }),
    });
    if (res.ok) {
      const data = await res.json();
      setListingId(data.id);
      return data;
    }

    // Listing may have been saved before a post-commit Celery hook failed (500).
    const existing = await fetchListingForProperty(property.id, token());
    if (existing) {
      setListingId(existing.id);
      return existing;
    }

    const err = await res.json();
    throw new Error(err.detail || JSON.stringify(err));
  };

  const ensureListing = async (listingType, availableFrom) => {
    if (listingId) {
      return { id: listingId };
    }
    const existing = await fetchListingForProperty(property.id, token());
    if (existing) {
      setListingId(existing.id);
      return existing;
    }
    return createListing(listingType, availableFrom);
  };

  const updateListingDetails = async (lid, type, details) => {
    try {
      const endpoint = type === 'rent'
        ? apiRoutes.listings.rentDetails(lid)
        : apiRoutes.listings.saleDetails(lid);
      await apiRequest(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(details),
      });
    } catch { /* non-fatal — can be edited later */ }
  };

  const handleStepSubmit = async (payload) => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    setStepData(prev => ({ ...prev, [activeStep]: payload }));

    try {
      let response;

      // ── Step 6: Listing choice ──────────────────────────────────────────────
      if (activeStep === 6) {
        response = await apiRequest(
          apiRoutes.propertyWizard.listingChoice(property.id),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify({
              create_listing: payload.create_listing,
              listing_type: payload.listing_type,
              available_from: payload.available_from,
            }),
          }
        );

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || JSON.stringify(err));
        }

        const data = await response.json();
        setWantList(data.create_listing);

        if (data.create_listing && data.listing_type) {
          const listing = await ensureListing(data.listing_type, data.available_from);

          if (listing && listing.id) {
            if (data.listing_type === 'RENT' && payload.rent_details) {
              await updateListingDetails(listing.id, 'rent', payload.rent_details);
            } else if (data.listing_type === 'SALE' && payload.sale_details) {
              await updateListingDetails(listing.id, 'sale', payload.sale_details);
            }
          }
        }

        setSavedSteps(prev => new Set([...prev, 6]));
        // If backend tells us to jump, honour it; otherwise just go to 7
        const next = data.next_step ?? 7;
        setActiveStep(next);
        setSaving(false);
        return;
      }

      // ── Step 7: Verification ────────────────────────────────────────────────
      if (activeStep === 7) {
        response = await apiRequest(
          apiRoutes.propertyWizard.step(property.id, '7-verify'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify({}),
          }
        );

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || JSON.stringify(err));
        }

        const verifyData = await response.json();
        if (!verifyData.ok) {
          const msgs = verifyData.issues?.map(i => i.message).join('\n') || 'اطلاعات ملک ناقص است';
          throw new Error(msgs);
        }

        setSavedSteps(prev => new Set([...prev, 7]));
        setActiveStep(8);
        setSaving(false);
        return;
      }

      // ── Step 8: Price ───────────────────────────────────────────────────────
      if (activeStep === 8) {
        let currentListingId = listingId;
        if (!currentListingId) {
          const existing = await fetchListingForProperty(property.id, token());
          if (existing) {
            currentListingId = existing.id;
            setListingId(currentListingId);
          }
        }
        if (!currentListingId) throw new Error('شناسه آگهی یافت نشد');

        response = await apiRequest(
          apiRoutes.propertyWizard.step(property.id, '8-price'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify({ ...payload, listing_id: currentListingId }),
          }
        );
      }

      // ── Step 9: Schedule ────────────────────────────────────────────────────
      else if (activeStep === 9) {
        const dayToWeekday = {
          MONDAY: 0, TUESDAY: 1, WEDNESDAY: 2, THURSDAY: 3,
          FRIDAY: 4, SATURDAY: 5, SUNDAY: 6,
        };

        let currentListingId = listingId;
        if (!currentListingId) throw new Error('شناسه آگهی یافت نشد');

        const slots = payload.visiting_schedule || [];
        if (slots.length === 0) throw new Error('لطفاً حداقل یک بازه زمانی انتخاب کنید');

        // Delete existing schedules then recreate
        const existingRes = await apiRequest(
          apiRoutes.listings.schedules(currentListingId),
          { headers: { Authorization: `Bearer ${token()}` } }
        );
        if (existingRes.ok) {
          const existing = await existingRes.json();
          await Promise.all(existing.map(s =>
            apiRequest(apiRoutes.listings.schedule(currentListingId, s.id), {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token()}` },
            })
          ));
        }

        for (const slotId of slots) {
          const dashIdx = slotId.indexOf('-');
          const day = slotId.substring(0, dashIdx);
          const times = slotId.substring(dashIdx + 1);
          const [startTime, endTime] = times.split('-');
          await apiRequest(apiRoutes.listings.schedules(currentListingId), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify({ weekday: dayToWeekday[day], start_time: startTime, end_time: endTime }),
          });
        }

        response = await apiRequest(
          apiRoutes.propertyWizard.completeStep(property.id, '9-schedule'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify({ listing_id: currentListingId }),
          }
        );
      }

      // ── Step 10: Summary ────────────────────────────────────────────────────
      else if (activeStep === 10) {
        response = await apiRequest(
          apiRoutes.propertyWizard.completeStep(property.id, '10-summary'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify({ name: payload.name, description: payload.description || '' }),
          }
        );
      }

      // ── Step 11: Finalize ───────────────────────────────────────────────────
      else if (activeStep === 11) {
        response = await apiRequest(
          apiRoutes.propertyWizard.finalize(property.id),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          // Promote listing from DRAFT to HIDDEN
          if (listingId) {
            await apiRequest(apiRoutes.listings.detail(listingId), {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
              body: JSON.stringify({ lifecycle_status: 'HIDDEN' }),
            });
          }
          setSavedSteps(prev => new Set([...prev, 11]));
          setCompleted(true);
          onSaved();
          setSaving(false);
          return;
        }
      }

      if (!response.ok) {
        const err = await response.json();
        throw new Error(
          err.detail ||
          (Array.isArray(err.non_field_errors) ? err.non_field_errors[0] : null) ||
          JSON.stringify(err)
        );
      }

      setSavedSteps(prev => new Set([...prev, activeStep]));
      setActiveStep(prev => prev + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreviousStep = () => {
    if (activeStep > 6) {
      setActiveStep(prev => prev - 1);
      setError(null);
      setSuccessMsg(null);
    }
  };

  const currentStepIndex = LISTING_STEPS.findIndex(s => s.key === activeStep);
  const progressPct = Math.round(((currentStepIndex + 1) / LISTING_STEPS.length) * 100);

  const renderStep = () => {
    if (completed) {
      return (
        <div className="completion-screen" style={{ margin: '60px auto', maxWidth: 480, textAlign: 'center' }}>
          <h3>آگهی با موفقیت ایجاد شد</h3>
          <p className="completion-message">آگهی شما ثبت شده و در صف بررسی قرار دارد.</p>
          <button className="submit-button" onClick={onClose}>بستن</button>
        </div>
      );
    }

    switch (activeStep) {
      case 6:
        return (
          <ListingStep
            onSubmit={handleStepSubmit}
            initialData={stepData[6]}
            onPreviousStep={handlePreviousStep}
            draftId={property.id}
          />
        );
      case 7:
        return (
          <DataVerificationStep
            onSubmit={handleStepSubmit}
            initialData={stepData[7]}
            onPreviousStep={handlePreviousStep}
          />
        );
      case 8:
        return (
          <AIPriceSuggestionStep
            onSubmit={handleStepSubmit}
            initialData={stepData[8]}
            onPreviousStep={handlePreviousStep}
            listingData={stepData[6]}
            listingId={listingId}
          />
        );
      case 9:
        return (
          <BuyerVisitScheduleStep
            onSubmit={handleStepSubmit}
            initialData={stepData[9]}
            onPreviousStep={handlePreviousStep}
            listingId={listingId}
          />
        );
      case 10:
        return (
          <SummaryStep
            onSubmit={handleStepSubmit}
            initialData={stepData[10]}
            onPreviousStep={handlePreviousStep}
            propertyId={property.id}
          />
        );
      case 11:
        return (
          <AgreementPaymentPublishStep
            onSubmit={handleStepSubmit}
            initialData={stepData[11]}
            onPreviousStep={handlePreviousStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="property-edit-fullscreen">
      {/* Header */}
      <div className="property-edit-header">
        <div className="property-edit-title">
          ایجاد آگهی: {property.name || `ملک ${property.id}`}
        </div>

        <div className="property-edit-tabs">
          {LISTING_STEPS.map((s, i) => (
            <button
              key={s.key}
              className={`edit-tab ${activeStep === s.key ? 'active' : ''} ${savedSteps.has(s.key) ? 'done' : ''}`}
              onClick={() => {
                // Only allow jumping to already-visited steps
                if (savedSteps.has(s.key) || s.key === activeStep) {
                  setActiveStep(s.key);
                  setError(null);
                  setSuccessMsg(null);
                }
              }}
              disabled={completed}
            >
              <span className="edit-tab-num">{i + 1}</span>
              <span className="edit-tab-label">{s.label}</span>
            </button>
          ))}
        </div>

        <button className="property-edit-close" style={{ opacity: 0.7 }} onClick={onClose}>
          بستن
        </button>
      </div>

      {/* Progress */}
      <div className="property-edit-progress">
        <div className="property-edit-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Banners */}
      {error && (
        <div className="error-message" style={{ margin: '8px 16px 0' }}>{error}</div>
      )}
      {successMsg && (
        <div className="success-message" style={{ margin: '8px 16px 0' }}>{successMsg}</div>
      )}

      {/* Saving overlay */}
      {saving && (
        <div className="loading-overlay">
          <div className="loading-text">در حال ذخیره...</div>
        </div>
      )}

      {/* Step content */}
      <div className="property-edit-content" ref={contentRef}>
        {renderStep()}
      </div>
    </div>
  );
}

export default ListingCreateModal;
